/**
 * Formateo para presentación. Se conserva el comportamiento exacto de
 * `formatCLP` y `formatPct` del CRM original, incluido el guion largo para
 * valores nulos y el signo `+` en porcentajes positivos.
 */
/**
 * Pesos chilenos sin decimales. `$1.102.080`.
 * Devuelve `'—'` para nulo o `NaN`, igual que el original.
 */
export function formatearCLP(valor) {
    if (valor == null || Number.isNaN(valor))
        return '—';
    return '$' + Math.round(valor).toLocaleString('es-CL');
}
/** Como `formatearCLP` pero abreviado para KPI apretados: `$1,1M`, `$450K`. */
export function formatearCLPCompacto(valor) {
    if (valor == null || Number.isNaN(valor))
        return '—';
    const abs = Math.abs(valor);
    const signo = valor < 0 ? '-' : '';
    if (abs >= 1_000_000_000)
        return `${signo}$${(abs / 1_000_000_000).toFixed(1).replace('.', ',')}MM`;
    if (abs >= 1_000_000)
        return `${signo}$${(abs / 1_000_000).toFixed(1).replace('.', ',')}M`;
    if (abs >= 10_000)
        return `${signo}$${Math.round(abs / 1000)}K`;
    return formatearCLP(valor);
}
/** Porcentaje con un decimal y signo explícito: `+12.5%`. */
export function formatearPct(valor, decimales = 1) {
    if (valor == null || Number.isNaN(valor))
        return '—';
    return (valor > 0 ? '+' : '') + valor.toFixed(decimales) + '%';
}
/** Porcentaje sin signo forzado, para consumos y tasas: `82%`. */
export function formatearPorcentajeSimple(valor, decimales = 0) {
    if (valor == null || Number.isNaN(valor))
        return '—';
    return valor.toFixed(decimales) + '%';
}
/** `30 días`, `1 día`, `—`. */
export function formatearDias(valor) {
    if (valor == null || Number.isNaN(valor))
        return '—';
    const redondeado = Math.round(valor);
    return `${redondeado} ${Math.abs(redondeado) === 1 ? 'día' : 'días'}`;
}
/** `12 meses`, `1 mes`. */
export function formatearMeses(valor) {
    if (valor == null || Number.isNaN(valor))
        return '—';
    const redondeado = Math.round(valor * 10) / 10;
    return `${redondeado} ${redondeado === 1 ? 'mes' : 'meses'}`;
}
/**
 * Formatea un RUT chileno: `77123456-K`.
 * No valida el dígito verificador, solo presenta; la validación vive en Zod.
 */
export function formatearRut(rut) {
    if (!rut)
        return '—';
    const limpio = rut.replace(/[.\-\s]/g, '').toUpperCase();
    if (limpio.length < 2)
        return rut;
    const cuerpo = limpio.slice(0, -1);
    const dv = limpio.slice(-1);
    return `${Number(cuerpo).toLocaleString('es-CL')}-${dv}`;
}
/** Normaliza un RUT a la forma canónica sin puntos ni guion, en mayúscula. */
export function normalizarRut(rut) {
    return rut.replace(/[.\-\s]/g, '').toUpperCase();
}
/**
 * Dígito verificador de un RUT chileno (algoritmo módulo 11).
 * `cuerpo` es el número sin DV.
 */
export function calcularDigitoVerificador(cuerpo) {
    let suma = 0;
    let multiplicador = 2;
    for (let i = cuerpo.length - 1; i >= 0; i--) {
        suma += Number(cuerpo[i]) * multiplicador;
        multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
    }
    const resto = 11 - (suma % 11);
    if (resto === 11)
        return '0';
    if (resto === 10)
        return 'K';
    return String(resto);
}
/** Valida un RUT completo contra su dígito verificador. */
export function esRutValido(rut) {
    if (!rut)
        return false;
    const limpio = normalizarRut(rut);
    if (!/^\d{7,8}[0-9K]$/.test(limpio))
        return false;
    const cuerpo = limpio.slice(0, -1);
    const dv = limpio.slice(-1);
    return calcularDigitoVerificador(cuerpo) === dv;
}
/** Trunca conservando palabras completas cuando se puede. */
export function truncar(texto, largo) {
    if (texto.length <= largo)
        return texto;
    const corte = texto.slice(0, largo);
    const ultimoEspacio = corte.lastIndexOf(' ');
    return (ultimoEspacio > largo * 0.6 ? corte.slice(0, ultimoEspacio) : corte).trimEnd() + '…';
}
/**
 * Escapa un valor para CSV: comillas dobles duplicadas y todo entre comillas.
 * Es la misma lógica de `escapeCsv` del CRM original.
 */
export function escaparCSV(valor) {
    if (valor == null)
        return '""';
    let str = String(valor);
    if (/^[=+\-@\t\r]/.test(str)) {
        str = "'" + str;
    }
    return `"${str.replace(/"/g, '""')}"`;
}
/**
 * Marca de orden de bytes UTF-8. Sin esto, Excel en Windows abre los CSV con los
 * acentos rotos, y casi todo el contenido de este sistema está en español.
 *
 * Se construye desde el punto de código y no como carácter literal a propósito:
 * U+FEFF es invisible en el editor, así que un literal se pierde en cualquier
 * normalización de codificación del archivo sin que nadie lo note — hasta que
 * un usuario abre un export y ve "Licitación" como "LicitaciÃ³n".
 */
export const BOM_UTF8 = String.fromCharCode(0xfeff);
//# sourceMappingURL=formato.js.map